<template>
    <v-container fluid>
        <v-card>
            <v-card-title>
                <v-row dense align="center">
                    <v-col cols="12" md="6">
                        <div class="text-subtitle-1 font-weight-bold">
                            <v-icon left color="primary">palette</v-icon>Apariencia
                        </div>
                        <div class="text-caption grey--text text--darken-1">
                            Logos, colores y fuente del sistema. Puede editar los temas predeterminados, crear los suyos y programarlos por fechas.
                        </div>
                    </v-col>
                    <v-col cols="12" md="6" class="text-md-right">
                        <v-btn color="info" small outlined class="mr-2" :loading="cargando" @click="cargar" title="Actualizar">
                            <v-icon small>refresh</v-icon>
                        </v-btn>
                        <v-btn color="success" small outlined class="mr-2" v-if="permisos.crear" @click="nuevoTema">
                            <v-icon small left>add</v-icon> Nuevo tema
                        </v-btn>
                        <v-btn color="error" small outlined v-if="permisos.eliminar" @click="abrirRestaurar">
                            <v-icon small left>restart_alt</v-icon> Restaurar de fábrica
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-title>

            <v-card-text>
                <v-alert dense text :color="vigente.origen === 'programacion' ? 'info' : 'primary'" border="left" class="mb-4" v-if="vigente.nombre">
                    <div class="d-flex flex-wrap align-center" style="gap: 6px 12px;">
                        <span>
                            <v-icon small left>visibility</v-icon>
                            Hoy se muestra <b>{{ vigente.nombre }}</b>
                            <span v-if="vigente.origen === 'programacion'">por una programación activa que cubre la fecha.</span>
                            <span v-else-if="vigente.origen === 'manual'">por elección manual.</span>
                            <span v-else>(tema de fábrica).</span>
                        </span>
                        <span class="text-caption grey--text text--darken-1">
                            Orden de resolución: programación activa que cubre hoy (gana la de mayor prioridad) → tema activado manualmente → fábrica.
                        </span>
                    </div>
                </v-alert>

                <v-tabs v-model="pestana" color="primary" class="mb-4">
                    <v-tab>Temas <v-chip x-small class="ml-2">{{ temas.length }}</v-chip></v-tab>
                    <v-tab>Programación por fechas <v-chip x-small class="ml-2">{{ programaciones.length }}</v-chip></v-tab>
                </v-tabs>

                <v-tabs-items v-model="pestana">
                    <!-- Temas -->
                    <v-tab-item>
                        <v-row v-if="temas.length">
                            <v-col cols="12" sm="6" lg="4" v-for="tema in temas" :key="tema.tema_id">
                                <v-card outlined class="tema" :class="{'tema--vigente': tema.es_vigente, 'tema--inactivo': !tema.activo}">
                                    <div class="tema__franja" :style="{background: `linear-gradient(135deg, ${tema.colores.primario} 0%, ${tema.colores.primario} 55%, ${tema.colores.acento} 100%)`}">
                                        <img :src="logoUrl(tema, 'gobierno')" alt="Logo" class="tema__logo">
                                        <span class="tema__fuente" :style="{fontFamily: `'${tema.fuente.familia}', sans-serif`}">Aa</span>
                                    </div>
                                    <v-card-text class="pb-2">
                                        <div class="d-flex align-start">
                                            <div class="flex-grow-1">
                                                <div class="text-subtitle-2 font-weight-bold">{{ tema.nombre }}</div>
                                                <div class="text-caption grey--text text--darken-1 tema__descripcion">{{ tema.descripcion || 'Sin descripción.' }}</div>
                                            </div>
                                        </div>
                                        <div class="mt-2 d-flex flex-wrap" style="gap: 4px;">
                                            <v-chip x-small label :color="tema.predeterminado ? 'primary' : 'teal'" outlined>{{ tema.predeterminado ? 'Predeterminado' : 'Personalizado' }}</v-chip>
                                            <v-chip x-small label color="success" text-color="white" v-if="tema.es_vigente">Vigente hoy</v-chip>
                                            <v-chip x-small label color="info" text-color="white" v-if="tema.es_manual">Manual</v-chip>
                                            <v-chip x-small label color="orange" text-color="white" v-if="tema.modificado">Modificado</v-chip>
                                            <v-chip x-small label color="grey" text-color="white" v-if="!tema.activo">Inactivo</v-chip>
                                            <v-chip x-small label outlined v-if="tema.programaciones">
                                                <v-icon x-small left>event</v-icon>{{ tema.programaciones_activas }}/{{ tema.programaciones }} programadas
                                            </v-chip>
                                        </div>
                                        <div class="mt-3 d-flex align-center" style="gap: 6px;">
                                            <span v-for="(valor, clave) in tema.colores" :key="clave" class="tema__muestra" :style="{background: valor}" :title="`${clave}: ${valor}`"></span>
                                            <span class="text-caption grey--text ml-1">{{ tema.fuente.familia }}</span>
                                        </div>
                                    </v-card-text>
                                    <v-divider></v-divider>
                                    <v-card-actions class="px-3">
                                        <v-btn small text color="primary" v-if="permisos.editar && !tema.es_manual && tema.activo" :loading="activando === tema.tema_id" @click="activar(tema)">
                                            <v-icon small left>check_circle</v-icon> Activar
                                        </v-btn>
                                        <v-spacer></v-spacer>
                                        <v-btn icon small color="info" v-if="permisos.editar" title="Editar" @click="editarTema(tema)"><v-icon small>edit</v-icon></v-btn>
                                        <v-btn icon small color="teal" v-if="permisos.crear" title="Duplicar como personalizado" :loading="duplicando === tema.tema_id" @click="duplicar(tema)"><v-icon small>content_copy</v-icon></v-btn>
                                        <v-btn icon small color="error" v-if="permisos.eliminar && !tema.predeterminado" title="Eliminar" @click="confirmarEliminarTema(tema)"><v-icon small>delete</v-icon></v-btn>
                                    </v-card-actions>
                                </v-card>
                            </v-col>
                        </v-row>
                        <v-alert v-else-if="!cargando" dense type="info" outlined class="text-center mt-3">No hay temas registrados.</v-alert>
                    </v-tab-item>

                    <!-- Programación -->
                    <v-tab-item>
                        <div class="d-flex flex-wrap align-center mb-3" style="gap: 8px;">
                            <div class="text-caption grey--text text--darken-1 flex-grow-1">
                                Una programación activa cambia el tema automáticamente entre las fechas indicadas. Las de los meses conmemorativos ya están creadas: actívelas con el interruptor.
                            </div>
                            <v-btn color="success" small outlined v-if="permisos.crear" @click="nuevaProgramacion">
                                <v-icon small left>add</v-icon> Nueva programación
                            </v-btn>
                        </div>
                        <v-data-table
                            dense
                            :headers="encabezadosProgramacion"
                            :items="programaciones"
                            :loading="cargando"
                            item-key="programacion_id"
                            :items-per-page="15"
                            :footer-props="$store.state.footerPropsTable"
                        >
                            <template v-slot:[`item.tema`]="{ item }">
                                <span class="tema__muestra tema__muestra--chica" :style="{background: colorTema(item.tema_id)}"></span>
                                {{ item.tema }}
                            </template>
                            <template v-slot:[`item.fecha_desde`]="{ item }">
                                {{ fechaProgramacion(item, item.fecha_desde) }}
                            </template>
                            <template v-slot:[`item.fecha_hasta`]="{ item }">
                                {{ fechaProgramacion(item, item.fecha_hasta) }}
                            </template>
                            <template v-slot:[`item.recurrente_anual`]="{ item }">
                                <v-chip x-small label :color="item.recurrente_anual ? 'primary' : 'grey'" outlined>{{ item.recurrente_anual ? 'Cada año' : 'Una vez' }}</v-chip>
                            </template>
                            <template v-slot:[`item.cubre_hoy`]="{ item }">
                                <v-icon small :color="item.cubre_hoy ? 'success' : 'grey lighten-1'">{{ item.cubre_hoy ? 'today' : 'event_busy' }}</v-icon>
                                <span class="text-caption ml-1" :class="item.cubre_hoy ? '' : 'grey--text'">{{ item.cubre_hoy ? 'Cubre hoy' : 'No cubre hoy' }}</span>
                            </template>
                            <template v-slot:[`item.activo`]="{ item }">
                                <v-switch :input-value="item.activo" inset dense hide-details color="success" class="mt-0"
                                          :disabled="!permisos.editar" :loading="activandoProgramacion === item.programacion_id"
                                          @change="activarProgramacion(item, $event)"/>
                            </template>
                            <template v-slot:[`item.fecha_actualiza`]="{ item }">
                                {{ item.fecha_actualiza | fechaESPHora }}
                            </template>
                            <template v-slot:[`item.acciones`]="{ item }">
                                <v-btn icon small color="info" v-if="permisos.editar" title="Editar" @click="editarProgramacion(item)"><v-icon small>edit</v-icon></v-btn>
                                <v-btn icon small color="error" v-if="permisos.eliminar" title="Eliminar" @click="confirmarEliminarProgramacion(item)"><v-icon small>delete</v-icon></v-btn>
                            </template>
                            <template v-slot:no-data>
                                <v-alert dense type="info" outlined class="text-center mt-3">Aún no hay programaciones.</v-alert>
                            </template>
                        </v-data-table>
                    </v-tab-item>
                </v-tabs-items>
            </v-card-text>
        </v-card>

        <tema-formulario v-model="dialogoTema" :tema="temaEditar" :fuentes="fuentes" @guardado="aplicarDatos"/>
        <programacion-formulario v-model="dialogoProgramacion" :programacion="programacionEditar" :temas="temas" @guardado="aplicarDatos"/>

        <!-- Eliminar tema o programación -->
        <v-dialog v-model="dialogoEliminar" max-width="440">
            <v-card>
                <v-card-title class="text-subtitle-1 font-weight-bold">{{ aEliminar && aEliminar.tipo === 'tema' ? 'Eliminar tema' : 'Eliminar programación' }}</v-card-title>
                <v-card-text v-if="aEliminar">
                    <template v-if="aEliminar.tipo === 'tema'">
                        ¿Eliminar el tema <b>{{ aEliminar.fila.nombre }}</b>? Se borran también sus logos y sus programaciones.
                        Solo se puede eliminar si no está activo como manual ni lo usa una programación activa.
                    </template>
                    <template v-else>
                        ¿Eliminar la programación de <b>{{ aEliminar.fila.tema }}</b>
                        ({{ aEliminar.fila.fecha_desde | fechaESP }} a {{ aEliminar.fila.fecha_hasta | fechaESP }})?
                    </template>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="dialogoEliminar = false">Cancelar</v-btn>
                    <v-btn color="error" depressed :loading="guardando" @click="eliminar">Eliminar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Restaurar de fábrica -->
        <v-dialog v-model="dialogoRestaurar" max-width="520" persistent>
            <v-card>
                <v-card-title class="text-subtitle-1 font-weight-bold">
                    <v-icon left color="error">restart_alt</v-icon> Restaurar de fábrica
                </v-card-title>
                <v-card-text>
                    <p>Esta acción:</p>
                    <ul class="mb-3">
                        <li>devuelve los temas predeterminados a su configuración original y quita los logos subidos en ellos;</li>
                        <li>vuelve a mostrar el tema <b>INFOP (fábrica)</b>;</li>
                        <li>desactiva todas las programaciones (no las borra).</li>
                    </ul>
                    <p>Los temas personalizados <b>no</b> se eliminan.</p>
                    <v-text-field v-model="confirmacionRestaurar" label="Escriba RESTAURAR para continuar" outlined dense autocomplete="off"
                                  @keyup.enter="restaurar"/>
                </v-card-text>
                <v-card-actions class="px-6 pb-4">
                    <v-spacer></v-spacer>
                    <v-btn text @click="dialogoRestaurar = false" :disabled="guardando">Cancelar</v-btn>
                    <v-btn color="error" depressed :loading="guardando" :disabled="confirmacionRestaurar.trim() !== 'RESTAURAR'" @click="restaurar">Restaurar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import Auth from "@/services/auth";
import TemaFormulario from "@/components/Apariencia/TemaFormulario.vue";
import ProgramacionFormulario from "@/components/Apariencia/ProgramacionFormulario.vue";
import {FUENTES, logosPredeterminados, recargarApariencia, urlAbsoluta} from "@/services/apariencia";

/**
 * Administración → Apariencia: temas (predeterminados y personalizados),
 * tema manual, programación por fechas y restauración de fábrica.
 */
export default {
    name: "AdministracionApariencia",
    components: {TemaFormulario, ProgramacionFormulario},
    data() {
        return {
            cargando: false,
            guardando: false,
            activando: null,
            duplicando: null,
            activandoProgramacion: null,
            pestana: 0,
            temas: [],
            programaciones: [],
            fuentes: FUENTES,
            vigente: {tema_id: null, nombre: '', origen: 'fabrica'},
            temaManualId: null,
            permisos: {crear: false, editar: false, eliminar: false},
            dialogoTema: false,
            temaEditar: null,
            dialogoProgramacion: false,
            programacionEditar: null,
            dialogoEliminar: false,
            aEliminar: null,
            dialogoRestaurar: false,
            confirmacionRestaurar: '',
            encabezadosProgramacion: [
                {text: 'Tema', value: 'tema'},
                {text: 'Desde', value: 'fecha_desde'},
                {text: 'Hasta', value: 'fecha_hasta'},
                {text: 'Repetición', value: 'recurrente_anual'},
                {text: 'Prioridad', value: 'prioridad', align: 'end'},
                {text: 'Hoy', value: 'cubre_hoy', sortable: false},
                {text: 'Activa', value: 'activo', sortable: false},
                {text: 'Actualizada', value: 'fecha_actualiza'},
                {text: '', value: 'acciones', sortable: false, align: 'end'},
            ],
        };
    },
    methods: {
        cargar() {
            this.cargando = true;
            window.axios.get('/apariencia/temas').then(response => {
                if (response.data.success) {
                    this.aplicarDatos(response.data.data);
                } else {
                    this.$toast.error(response.data.message);
                }
            })
                .catch(() => this.$toast.error('No se pudo cargar la apariencia.'))
                .finally(() => {
                    this.cargando = false;
                });
        },
        /** Toma la respuesta del API (lista completa) y refresca la apariencia aplicada a la aplicación. */
        aplicarDatos(datos) {
            if (!datos) return;
            this.temas = datos.temas || [];
            this.programaciones = datos.programaciones || [];
            this.vigente = datos.vigente || this.vigente;
            this.temaManualId = datos.tema_manual_id || null;
            if (datos.fuentes) this.fuentes = datos.fuentes;
            if (datos.permisos) this.permisos = datos.permisos;
            recargarApariencia();
        },
        logoUrl(tema, tipo) {
            return urlAbsoluta(tema.logos && tema.logos[tipo]) || logosPredeterminados[tipo];
        },
        colorTema(temaId) {
            const tema = this.temas.find(t => t.tema_id === temaId);
            return tema ? tema.colores.primario : '#9E9E9E';
        },
        diaMes(fecha) {
            const [, mes, dia] = String(fecha || '').split('-');
            const meses = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
            return mes && dia ? `${dia} ${meses[Number(mes) - 1] || mes}` : fecha;
        },
        /** Recurrente: solo día y mes ("01 sep"); puntual: fecha completa. */
        fechaProgramacion(programacion, fecha) {
            return programacion.recurrente_anual ? this.diaMes(fecha) : this.$options.filters.fechaESP(fecha);
        },
        nuevoTema() {
            this.temaEditar = null;
            this.dialogoTema = true;
        },
        editarTema(tema) {
            this.temaEditar = tema;
            this.dialogoTema = true;
        },
        activar(tema) {
            this.activando = tema.tema_id;
            this.enviar('/apariencia/activar', {tema_id: tema.tema_id}, 'No se pudo activar el tema.')
                .finally(() => {
                    this.activando = null;
                });
        },
        duplicar(tema) {
            this.duplicando = tema.tema_id;
            this.enviar('/apariencia/temas/duplicar', {tema_id: tema.tema_id}, 'No se pudo duplicar el tema.')
                .finally(() => {
                    this.duplicando = null;
                });
        },
        confirmarEliminarTema(tema) {
            this.aEliminar = {tipo: 'tema', fila: tema};
            this.dialogoEliminar = true;
        },
        confirmarEliminarProgramacion(programacion) {
            this.aEliminar = {tipo: 'programacion', fila: programacion};
            this.dialogoEliminar = true;
        },
        eliminar() {
            if (!this.aEliminar) return;
            this.guardando = true;
            const esTema = this.aEliminar.tipo === 'tema';
            const ruta = esTema ? '/apariencia/temas/eliminar' : '/apariencia/programaciones/eliminar';
            const datos = esTema ? {tema_id: this.aEliminar.fila.tema_id} : {programacion_id: this.aEliminar.fila.programacion_id};

            this.enviar(ruta, datos, 'No se pudo eliminar.')
                .then(ok => {
                    if (ok) this.dialogoEliminar = false;
                })
                .finally(() => {
                    this.guardando = false;
                });
        },
        nuevaProgramacion() {
            this.programacionEditar = null;
            this.dialogoProgramacion = true;
        },
        editarProgramacion(programacion) {
            this.programacionEditar = programacion;
            this.dialogoProgramacion = true;
        },
        activarProgramacion(programacion, activo) {
            this.activandoProgramacion = programacion.programacion_id;
            this.enviar('/apariencia/programaciones/activar', {programacion_id: programacion.programacion_id, activo: activo ? 1 : 0}, 'No se pudo cambiar la programación.')
                .finally(() => {
                    this.activandoProgramacion = null;
                });
        },
        abrirRestaurar() {
            this.confirmacionRestaurar = '';
            this.dialogoRestaurar = true;
        },
        restaurar() {
            if (this.confirmacionRestaurar.trim() !== 'RESTAURAR') return;
            this.guardando = true;
            this.enviar('/apariencia/restaurar', {confirmacion: 'RESTAURAR'}, 'No se pudo restaurar la apariencia.')
                .then(ok => {
                    if (ok) this.dialogoRestaurar = false;
                })
                .finally(() => {
                    this.guardando = false;
                });
        },
        /** POST común: muestra el mensaje, aplica los datos devueltos y resuelve true si salió bien. */
        enviar(ruta, datos, mensajeError) {
            return window.axios.post(ruta, datos).then(response => {
                if (response.data.success) {
                    this.$toast.success(response.data.message, {timeout: 8000});
                    this.aplicarDatos(response.data.data);
                    return true;
                }
                this.$toast.error(response.data.message, {timeout: 10000});
                return false;
            })
                .catch(error => {
                    const cuerpo = error.response && error.response.data;
                    this.$toast.error((cuerpo && cuerpo.message) || mensajeError, {timeout: 10000});
                    return false;
                });
        },
    },
    created() {
        Auth.checkPermisos('/administracion/apariencia').then(() => this.cargar());
    },
}
</script>

<style scoped lang="scss">
.tema {
    height: 100%;
    display: flex;
    flex-direction: column;
    transition: box-shadow .15s ease;

    &--vigente {
        border-color: #4CAF50 !important;
        box-shadow: 0 0 0 2px rgba(76, 175, 80, .25);
    }

    &--inactivo {
        opacity: .7;
    }

    .v-card__text {
        flex: 1;
    }

    &__franja {
        height: 64px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0 14px;
        border-radius: 4px 4px 0 0;
    }

    &__logo {
        height: 32px;
        width: auto;
        max-width: 60%;
        background: #fff;
        border-radius: 6px;
        padding: 3px 8px;
        object-fit: contain;
    }

    &__fuente {
        color: #fff;
        font-size: 26px;
        font-weight: 700;
        text-shadow: 0 1px 2px rgba(0, 0, 0, .3);
    }

    &__descripcion {
        min-height: 32px;
    }

    &__muestra {
        display: inline-block;
        width: 22px;
        height: 22px;
        border-radius: 6px;
        border: 1px solid rgba(0, 0, 0, .15);

        &--chica {
            width: 12px;
            height: 12px;
            border-radius: 3px;
            vertical-align: middle;
            margin-right: 4px;
        }
    }
}
</style>
